import {
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  ComposedChart,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import type { DiveSample } from "@/lib/shearwater-parser";

interface DiveChartProps {
  samples: DiveSample[];
  showTemperature?: boolean;
  showNdl?: boolean;
}

// Explicit colors for better visibility and coordination
const COLORS = {
  depth: "#3b82f6", // Blue - primary
  temperature: "#fdba74", // Orange - dimmer/lighter
  ndl: "#f87171", // Red - dimmer/lighter
};

const chartConfig = {
  depth: {
    label: "Depth (ft)",
    color: COLORS.depth,
  },
  temperature: {
    label: "Temp (°F)",
    color: COLORS.temperature,
  },
  ndl: {
    label: "NDL (min)",
    color: COLORS.ndl,
  },
} satisfies ChartConfig;

export function DiveChart({
  samples,
  showTemperature = true,
  showNdl = false,
}: DiveChartProps) {
  // Transform samples for chart - use decimal minutes for smooth curves
  const chartData = samples.map((s) => ({
    time: s.elapsedSeconds / 60, // decimal minutes for smooth interpolation
    depth: s.depthFeet,
    temperature: s.temperatureFahrenheit,
    ndl: s.ndlMinutes,
  }));

  // Calculate Y-axis domain for depth
  const maxDepth = Math.max(...samples.map((s) => s.depthFeet));
  const depthCeiling = Math.ceil(maxDepth / 10) * 10 + 10; // Round up + padding

  // Generate explicit ticks starting from 0
  const depthTicks = Array.from(
    { length: Math.floor(depthCeiling / 20) + 1 },
    (_, i) => i * 20
  );

  // Generate explicit time ticks (every 10 minutes) up to actual dive time
  const maxTime = Math.max(...chartData.map((d) => d.time));
  const timeTicks = Array.from(
    { length: Math.floor(maxTime / 10) + 1 },
    (_, i) => i * 10
  );

  return (
    <ChartContainer
      config={chartConfig}
      className="h-[250px] sm:h-[300px] w-full"
    >
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="time"
          type="number"
          scale="linear"
          domain={[0, maxTime]}
          ticks={timeTicks}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `${value}m`}
          tick={{ fontSize: 11, fill: "#9ca3af", fillOpacity: 0.7 }}
          allowDataOverflow={true}
        />
        <YAxis
          yAxisId="depth"
          domain={[0, depthCeiling]}
          ticks={depthTicks}
          reversed={true}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: COLORS.depth }}
          tickFormatter={(value) => `${value}ft`}
          width={30}
        />
        {showTemperature && (
          <YAxis
            yAxisId="temp"
            orientation="right"
            domain={["dataMin - 2", "dataMax + 2"]}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: COLORS.temperature }}
            tickFormatter={(value) => `${value}°`}
            width={28}
          />
        )}
        {showNdl && (
          <YAxis
            yAxisId="ndl"
            orientation="right"
            domain={[0, 99]}
            ticks={[0, 25, 50, 75, 99]}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10, fill: COLORS.ndl }}
            tickFormatter={(value) => `${value}m`}
            width={28}
          />
        )}
        <ChartTooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            return (
              <div className="bg-background border border-border/50 rounded-lg px-3 py-2 shadow-xl text-sm space-y-1">
                {payload.map((item) => {
                  if (item.dataKey === "depth") {
                    return (
                      <div key="depth" className="text-foreground">
                        <span className="font-medium">{item.value}ft</span>{" "}
                        <span className="text-muted-foreground">Depth</span>
                      </div>
                    );
                  }
                  if (item.dataKey === "temperature") {
                    return (
                      <div key="temp" className="text-foreground">
                        <span className="font-medium">{item.value}°F</span>{" "}
                        <span className="text-muted-foreground">Temp</span>
                      </div>
                    );
                  }
                  if (item.dataKey === "ndl") {
                    return (
                      <div key="ndl" className="text-foreground">
                        <span className="font-medium">{item.value}m</span>{" "}
                        <span className="text-muted-foreground">NDL</span>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            );
          }}
        />
        <Area
          yAxisId="depth"
          type="monotone"
          dataKey="depth"
          stroke={COLORS.depth}
          fill={COLORS.depth}
          fillOpacity={0.3}
          strokeWidth={2}
          baseValue={0}
          isAnimationActive={false}
        />
        {showTemperature && (
          <Line
            yAxisId="temp"
            type="monotone"
            dataKey="temperature"
            stroke={COLORS.temperature}
            strokeWidth={1.5}
            strokeOpacity={0.7}
            dot={false}
            isAnimationActive={false}
          />
        )}
        {showNdl && (
          <Line
            yAxisId="ndl"
            type="monotone"
            dataKey="ndl"
            stroke={COLORS.ndl}
            strokeWidth={1.5}
            strokeOpacity={0.6}
            strokeDasharray="4 4"
            dot={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ChartContainer>
  );
}
